/**
 * Модули — разметка 1:1 с legacy htdocs/core/templates/admin_module.tpl:
 * .bs-callout, form.form-settings, table.table.table-hover.table-bordered.table-condensed
 * с колонками «Идентификатор модуля / Название / Конфигурационное значение / Группа /
 * Вывод в меню админки / Вывод в меню сайта», строка добавления и кнопка
 * «сохранить изменения». Поля первой записи (id = 1) заблокированы — как в legacy.
 *
 * Legacy отправлял одну POST-форму (upd_value[id], upd_group[id], upd_admin_value[id],
 * upd_used_value[id], module-delete[id]); здесь то же самое через
 * PUT/POST/DELETE /api/admin/system/modules (Handlers\Admin\System, только суперадмин).
 */

import { useEffect, useState } from 'react'

import { api, ApiError } from '@/api/client'
import { useLegacyCss } from '@/lib/legacyAssets'
import { useApi } from '@/lib/useApi'

/** Строка таблицы modules. */
interface ModuleRow {
  id: number
  module: string
  name: string
  conf_value: string
  group: string
  used_value: number | string
  admin_value: number | string
  orderongroup: number | string
}

/** Значения строки в форме. */
interface ModuleDraft {
  conf_value: string
  group: string
  admin_value: string
  used_value: string
}

export function AdminModulesPage() {
  useLegacyCss('admin', 'newapproach', 'docs.min')

  const { data, reload } = useApi<ModuleRow[]>('/api/admin/system/modules')

  const [rows, setRows] = useState<Record<number, ModuleDraft>>({})
  const [add, setAdd] = useState({
    module: '',
    name: '',
    conf_value: '',
    group: '',
    admin_value: '0',
    used_value: '0',
  })
  const [ok, setOk] = useState<string[]>([])
  const [error, setError] = useState<string[]>([])

  const modules = data ?? []

  useEffect(() => {
    const drafts: Record<number, ModuleDraft> = {}
    for (const item of modules) {
      drafts[item.id] = {
        conf_value: item.conf_value ?? '',
        group: String(item.group ?? ''),
        admin_value: String(item.admin_value ?? '0'),
        used_value: String(item.used_value ?? '0'),
      }
    }
    setRows(drafts)
  }, [data])

  const patch = (id: number, field: keyof ModuleDraft, value: string): void => {
    setRows((current) => {
      const row = current[id]
      if (row === undefined) return current
      return { ...current, [id]: { ...row, [field]: value } }
    })
  }

  /** legacy settings-submit: сохраняет все строки (кроме id = 1) и добавляет новую. */
  const submit = async (): Promise<void> => {
    try {
      for (const item of modules) {
        if (item.id === 1) continue // legacy: поля первой записи disabled
        const draft = rows[item.id]
        if (draft === undefined) continue
        await api.put(`/api/admin/system/modules/${item.id}`, draft)
      }
      if (add.module !== '' || add.name !== '' || add.conf_value !== '') {
        await api.post('/api/admin/system/modules', add)
        setAdd({
          module: '',
          name: '',
          conf_value: '',
          group: '',
          admin_value: '0',
          used_value: '0',
        })
      }
      setOk(['Изменения существующих настроек сохранены.'])
      setError([])
      reload()
    } catch (e: unknown) {
      setError([e instanceof ApiError ? e.message : 'Ошибка при сохранении.'])
      setOk([])
    }
  }

  /** legacy module-delete[id]. */
  const remove = async (id: number): Promise<void> => {
    try {
      const result = await api.delete<{ message: string }>(`/api/admin/system/modules/${id}`)
      setOk([result.message])
      setError([])
      reload()
    } catch (e: unknown) {
      setError([e instanceof ApiError ? e.message : 'Ошибка при удалении.'])
      setOk([])
    }
  }

  return (
    <>
      {error.length > 0 && (
        <div className="bs-callout bs-callout-danger">
          <h4>Ошибка</h4>
          {error.map((message, index) => (
            <p key={index}>{message}</p>
          ))}
        </div>
      )}
      {ok.length > 0 && (
        <div className="bs-callout bs-callout-info">
          <h4>OK</h4>
          {ok.map((message, index) => (
            <p key={index}>{message}</p>
          ))}
        </div>
      )}

      <form
        className="form-settings"
        method="POST"
        onSubmit={(e) => {
          e.preventDefault()
          void submit()
        }}
      >
        <table className="table table-hover table-bordered table-condensed">
          <thead>
            <tr>
              <th>№</th>
              <th>Идентификатор модуля</th>
              <th>Название модуля</th>
              <th>
                Конфигурационное значение(0 = откл.)
                <br />
                напр.: для камер это ID дома
              </th>
              <th>
                Группа(0 = откл,
                <br /> 1 - Сайт,
                <br /> 2 - Электроэнергия,
                <br /> 3 - Рассылки)
              </th>
              <th>Вывод в меню админки</th>
              <th>Вывод в меню сайта</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {modules.map((item, index) => (
              <tr key={item.id}>
                <td>{index + 1}</td>
                <td>{item.module}</td>
                <td>{item.name}</td>
                <td>
                  <input
                    disabled={item.id === 1}
                    className="form-control"
                    name={`upd_value[${item.id}]`}
                    value={rows[item.id]?.conf_value ?? ''}
                    onChange={(e) => patch(item.id, 'conf_value', e.target.value)}
                  />
                </td>
                <td>
                  <input
                    disabled={item.id === 1}
                    className="form-control"
                    name={`upd_group[${item.id}]`}
                    value={rows[item.id]?.group ?? ''}
                    onChange={(e) => patch(item.id, 'group', e.target.value)}
                  />
                </td>
                <td>
                  <select
                    disabled={item.id === 1}
                    name={`upd_admin_value[${item.id}]`}
                    className="form-control"
                    value={rows[item.id]?.admin_value ?? '0'}
                    onChange={(e) => patch(item.id, 'admin_value', e.target.value)}
                  >
                    <option value="0">Нет</option>
                    <option value="1">Да</option>
                  </select>
                </td>
                <td>
                  <select
                    disabled={item.id === 1}
                    name={`upd_used_value[${item.id}]`}
                    className="form-control"
                    value={rows[item.id]?.used_value ?? '0'}
                    onChange={(e) => patch(item.id, 'used_value', e.target.value)}
                  >
                    <option value="0">Нет</option>
                    <option value="1">Да</option>
                  </select>
                </td>
                <td>
                  <input
                    disabled={item.id === 1}
                    type="submit"
                    name={`module-delete[${item.id}]`}
                    className="btn btn-xs btn-danger"
                    value="удалить"
                    onClick={(e) => {
                      e.preventDefault()
                      void remove(item.id)
                    }}
                  />
                </td>
              </tr>
            ))}
            <tr>
              <td />
              <td>
                <input
                  className="form-control"
                  name="module"
                  value={add.module}
                  onChange={(e) => setAdd({ ...add, module: e.target.value })}
                />
              </td>
              <td>
                <input
                  className="form-control"
                  name="name"
                  value={add.name}
                  onChange={(e) => setAdd({ ...add, name: e.target.value })}
                />
              </td>
              <td>
                <input
                  className="form-control"
                  name="conf_value"
                  value={add.conf_value}
                  onChange={(e) => setAdd({ ...add, conf_value: e.target.value })}
                />
              </td>
              <td>
                <input
                  className="form-control"
                  name="group"
                  value={add.group}
                  onChange={(e) => setAdd({ ...add, group: e.target.value })}
                />
              </td>
              <td>
                <select
                  name="admin_value"
                  className="form-control"
                  value={add.admin_value}
                  onChange={(e) => setAdd({ ...add, admin_value: e.target.value })}
                >
                  <option value="0">Нет</option>
                  <option value="1">Да</option>
                </select>
              </td>
              <td>
                <select
                  name="used_value"
                  className="form-control"
                  value={add.used_value}
                  onChange={(e) => setAdd({ ...add, used_value: e.target.value })}
                >
                  <option value="0">Нет</option>
                  <option value="1">Да</option>
                </select>
              </td>
              <td />
            </tr>
          </tbody>
        </table>
        <input
          type="submit"
          name="settings-submit"
          className="btn btn-xs btn-info"
          value="сохранить изменения"
        />
      </form>
    </>
  )
}
